import { ActionMenu } from '@components/ActionMenu';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Add, Delete, Edit } from '@mui/icons-material';
import { Divider, ListItemIcon, MenuItem } from '@mui/material';
import { useNotification } from '@utils/useNotification';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { fetchData } from 'gql/graphql-fetcher';
import { useBudgetItemsQuery, useGetProjectBudgetQuery } from 'gql/index';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useBudgetTableContext } from '../BudgetItemTable/context/useBudgetTableContext';

type QuoteActionsFragment = {
  id: number;
  name: string;
  amount: number;
  quoteDate: string;
  attachments?: { id: number; fileName: string; thumbnailStatus?: string }[];
}

interface Props {
  quote: QuoteActionsFragment;
  costItemId: number;
  size?: 'small' | 'medium';
  onEdit?: (quote: QuoteActionsFragment, costItemId: number) => void;
}

const deleteQuoteDocument = `
  mutation deleteQuote($input: DeleteQuoteInput!) {
    deleteQuote(input: $input) {
      quote { id }
      errors { ... on Error { message } }
    }
  }
`;

export const QuoteActions: React.FC<Props> = ({ quote, costItemId, size, onEdit }) => {
  const { formatMessage } = useIntl();
  const { projectId, canManageBudgetAndExpenses } = useCurrentProject();
  const { notifySuccess } = useNotification();
  const invalidateQuery = useQueryInvalidator();
  const budgetTableContext = useBudgetTableContext();
  const setEntryDialog = budgetTableContext?.setEntryDialog;

  const [isDeleting, setIsDeleting] = useState(false);

  const { mutate: deleteQuote, isPending: isDeletingQuote } = useMutation({
    mutationKey: ['deleteQuote'],
    mutationFn: (variables: { input: { projectId: number; id: number } }) =>
      fetchData(deleteQuoteDocument, variables)(),
    onSuccess: () => {
      notifySuccess(formatMessage({ id: 'Quote deleted successfully' }));
      invalidateQuery(useBudgetItemsQuery, { projectId });
      invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
    }
  });

  const handleDelete = () => {
    if (!projectId) return;
    deleteQuote({
      input: {
        projectId,
        id: quote.id
      }
    });
  };

  if (!canManageBudgetAndExpenses) {
    return null;
  }

  return (
    <>
      <ActionMenu buttonProps={{ size: size ?? 'medium' }} iconButtonProps={{ size: size ?? 'medium' }}>
        {budgetTableContext?.allowAddingExpenseToQuote && setEntryDialog && (
          <MenuItem onClick={() => setEntryDialog({ open: true, costItemId, type: 'expense', quote })}>
            <ListItemIcon>
              <Add />
            </ListItemIcon>
            {formatMessage({ id: 'Add expense' })}
          </MenuItem>
        )}
        <MenuItem onClick={() => (onEdit ? onEdit(quote, costItemId) : setEntryDialog?.({ open: true, costItemId, type: 'quote', quote }))}>
          <ListItemIcon>
            <Edit />
          </ListItemIcon>
          {formatMessage({ id: 'Edit' })}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setIsDeleting(true)}>
          <ListItemIcon>
            <Delete color='error'/>
          </ListItemIcon>
          {formatMessage({ id: 'Delete' })}
        </MenuItem>
      </ActionMenu>

      <ConfirmDialog
        open={isDeleting}
        onCancel={() => setIsDeleting(false)}
        onConfirm={handleDelete}
        title={formatMessage({ id: 'Delete quote' })}
        confirmColor='error'
        confirmText={formatMessage({ id: 'Delete' })}
        content={formatMessage({ id: 'Are you sure you want to delete this quote?' })}
        loading={isDeletingQuote}
      />

    </>
  );
};
